import { useMemo, useEffect } from 'react';
import 'highlight.js/styles/github.css';
import 'katex/dist/katex.min.css';
import { InlineChart } from '../chat/InlineChart';
import { hljs, parseAiMarkdown } from '../markdown/ai-marked';

function parseContentWithCharts(content: string) {
  const segments: Array<{ type: 'markdown'; content: string } | { type: 'chart'; code: string }> =
    [];
  const chartRegex = /```chart\s*([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = chartRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'markdown', content: content.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'chart', code: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({ type: 'markdown', content: content.slice(lastIndex) });
  }

  return segments;
}

function MarkdownSegment({ content }: { content: string }) {
  useEffect(() => {
    document.querySelectorAll('pre code').forEach((block) => {
      if (!block.classList.contains('hljs')) {
        hljs.highlightElement(block as HTMLElement);
      }
    });
  }, [content]);

  if (!content.trim()) return null;
  const html = parseAiMarkdown(content);
  return <div className="report-markdown" dangerouslySetInnerHTML={{ __html: html }} />;
}

interface ReportContentProps {
  content: string;
  className?: string;
}

/** Renders report body: markdown + inline charts from ```chart code blocks. */
export function ReportContent({ content, className }: ReportContentProps) {
  const segments = useMemo(() => parseContentWithCharts(content), [content]);

  if (!content.trim()) {
    return (
      <div className={className}>
        <p className="text-sm text-muted-foreground">No content yet.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {segments.map((seg, i) =>
        seg.type === 'markdown' ? (
          <MarkdownSegment key={i} content={seg.content} />
        ) : (
          <InlineChart key={i} code={seg.code} />
        )
      )}
    </div>
  );
}
